import axios, { type AxiosInstance } from 'axios'
import { HTTP_STATUS, STORAGE_KEYS } from '@/utils/constants';
import { storage } from '@/utils';
import type { ApiResponse, RequestConfig } from './types';

class ApiClient {
  private instance: AxiosInstance;
  
  constructor(config: RequestConfig = {}) {
    const baseURL = config.baseURL || import.meta.env.VITE_API_BASE_URL || '/api';
    const timeout = config.timeout || 10000;
    
    this.instance = axios.create({
      baseURL,
      timeout,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });
    
    this.setupInterceptors();
  }
  
  // 设置拦截器
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // 添加认证token
        const token = storage.get(STORAGE_KEYS.TOKEN);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // 添加请求ID
        config.headers['X-Request-ID'] = this.generateRequestId();
        
        // 开发环境下打印请求信息
        if (import.meta.env.VITE_DEBUG) {
          console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
        }
        
        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );
    
    // 响应拦截器
    this.instance.interceptors.response.use(
      (response) => {
        // 开发环境下打印响应信息
        if (import.meta.env.VITE_DEBUG) {
          console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
        }
        
        return response.data;
      },
      (error) => {
        const { response } = error;
        
        // 开发环境下打印错误信息
        if (import.meta.env.VITE_DEBUG) {
          console.error(`❌ API Error: ${response?.config.method?.toUpperCase()} ${response?.config.url}`, error);
        }
        
        // 处理401未授权
        if (response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.handleUnauthorized();
          return Promise.reject(new Error('登录已过期，请重新登录'));
        }
        
        // 处理403禁止访问
        if (response?.status === HTTP_STATUS.FORBIDDEN) {
          return Promise.reject(new Error('您没有权限访问此资源'));
        }
        
        // 处理404不存在
        if (response?.status === HTTP_STATUS.NOT_FOUND) {
          return Promise.reject(new Error('请求的资源不存在'));
        }
        
        // 处理500服务器错误
        if (response?.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
          return Promise.reject(new Error('服务器内部错误，请稍后再试'));
        }
        
        // 其他错误
        const errorMessage = response?.data?.message || error.message || '请求失败';
        return Promise.reject(new Error(errorMessage));
      }
    );
  }
  
  // 处理未授权
  private handleUnauthorized(): void {
    storage.remove(STORAGE_KEYS.TOKEN);
    storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
    storage.remove(STORAGE_KEYS.USER);
    
    // 如果不在登录页面，跳转到登录页
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  
  // 生成请求ID
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  // GET请求
  async get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    return this.instance.get(url, { params });
  }
  
  // POST请求
  async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.instance.post(url, data);
  }
  
  // PUT请求
  async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.instance.put(url, data);
  }
  
  // PATCH请求
  async patch<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.instance.patch(url, data);
  }
  
  // DELETE请求
  async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    return this.instance.delete(url);
  }
  
  // 上传文件
  async upload<T = any>(url: string, formData: FormData): Promise<ApiResponse<T>> {
    return this.instance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
  
  // 下载文件
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.instance.get(url, {
      responseType: 'blob',
    });
    
    // 创建下载链接
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

// 创建API客户端实例
export const apiClient = new ApiClient();
export default apiClient;